import { useState } from "react";
import { WaitlistDialog } from "./WaitlistDialog";
import { useNavigate } from "react-router-dom";
import { Tile } from "./ui/tile";

interface PromptLibraryProps {
  title?: string;
  description?: string;
  className?: string;
}

const PromptLibrary = ({ 
  title = "THE FUTURE MAKER'S TOOLKIT",
  description = `The Game Has Changed.
AI isn't coming for our jobs.
It's already transformed how we work.
But here's what matters: AI isn't replacing us.
It's amplifying us.
For the past 12 months, I've crafted and battle-tested these prompts in the real world. They've transformed me from a doer to a visionary. From a reader to an explorer. From overwhelmed to empowered.
Think of these prompts as your creative arsenal:

A lens to see possibilities where others see limitations
A microscope for deep analysis when you need precision
An accelerator for your learning and growth

This isn't about AI writing for you.
This is about AI thinking with you.`,
  className = ""
}: PromptLibraryProps) => {
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();

  const prompts = [
    {
      title: "101: Crafting a Memorable Brand Mark",
      description: "A comprehensive guide to creating a distinctive and memorable brand identity",
      goodFor: ["Brand Designers", "Startup Founders", "Marketing Teams"],
      image: "gradient-bg.jpg",
      isAvailable: false,
    },
    {
      title: "201: Product Strategy Framework",
      description: "Strategic framework for building products that resonate with your target market",
      goodFor: ["Product Managers", "Business Strategists", "Entrepreneurs"],
      image: "gradient-bg.jpg",
      isAvailable: false,
    },
    {
      title: "301: User Research Templates",
      description: "Ready-to-use templates for conducting effective user research",
      goodFor: ["UX Researchers", "Product Designers", "Product Managers"],
      image: "gradient-bg.jpg",
      isAvailable: false,
    },
    {
      title: "401: Design System Guidelines",
      description: "Comprehensive guide to creating and maintaining design systems",
      goodFor: ["UI Designers", "Design System Engineers", "Design Leads"],
      image: "gradient-bg.jpg",
      isAvailable: false,
    },
    {
      title: "501: Product Launch Playbook",
      description: "Step-by-step playbook for successful product launches",
      goodFor: ["Product Marketers", "Launch Managers", "Startup Teams"],
      image: "gradient-bg.jpg",
      isAvailable: false,
    },
  ];

  const handlePromptClick = (prompt: typeof prompts[0]) => {
    setSelectedPrompt(prompt.title);
    setIsDialogOpen(true);
  };

  return (
    <section className={`max-w-6xl mx-auto px-4 py-16 ${className}`}>
      <div className="flex justify-between items-center mb-8">
        <h1>{title}</h1>
      </div>
      {description && (
        <p className="text-gray-600 mb-8 whitespace-pre-line">{description}</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {prompts.map((prompt, index) => (
          <Tile
            key={index}
            title={prompt.title}
            description={prompt.description}
            onClick={() => handlePromptClick(prompt)}
            actionLabel="Join waitlist →"
          >
            <div className="space-y-2">
              <p className="text-[#221F26]/80 text-xs uppercase font-medium">Good for:</p>
              <div className="flex flex-wrap gap-2">
                {prompt.goodFor.map((item, idx) => (
                  <span 
                    key={idx} 
                    className="text-[#221F26]/90 text-xs bg-[#221F26]/10 px-2 py-1 rounded-full"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </Tile>
        ))}
      </div>

      <WaitlistDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        promptName={selectedPrompt || ""}
      />
    </section>
  );
};

export default PromptLibrary;